import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";
import type { ScheduledJourney } from "@tellescope/types-models";
import {
  applyMongoFilter,
  compileFreeformFilterBlock,
  createIdMap,
  type FreeformFieldOption,
  type FreeformFilterBlock,
} from "@tellescope/utils";
import { BlockProps } from "../types";
import { useBlock } from ".";

export interface JourneyRow {
  journeyId: string;
  title: string;
  state: string;
  isArchived: boolean;
}

export interface ScheduledJourneyRow {
  journeyId: string;
  title: string;
  addAt: Date;
}

export interface UseJourneysBlockArgs extends BlockProps {}

export const useJourneysBlock = ({
  enduserId,
  info,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  viewId,
  blockId,
  blockIndex,
  blockLayout,
  onViewMaterialized,
}: UseJourneysBlockArgs) => {
  const { t } = useTranslation("contacts");

  const block = useBlock({
    type: "information.journeys",
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    enduserId,
    viewId,
    blockId,
    blockIndex,
    blockLayout,
    onViewMaterialized,
    select: (enduser) => ({
      journeys: enduser.journeys ?? {},
      scheduledJourneys: enduser.scheduledJourneys ?? [],
    }),
  });

  const membership = useMemo(() => block.enduserQuery.data?.journeys ?? {}, [block.enduserQuery.data]);
  const scheduled = useMemo(() => block.enduserQuery.data?.scheduledJourneys ?? [], [block.enduserQuery.data]);

  const referencedJourneyIds = useMemo(
    () => [...new Set([...Object.keys(membership), ...scheduled.map(({ journeyId }) => journeyId)])],
    [membership, scheduled]
  );

  const referencedJourneysQuery = useTellescopeQuery({
    model: "journeys",
    method: "getByIds",
    request: { ids: referencedJourneyIds },
    enabled: referencedJourneyIds.length > 0,
    crossQueryCacheSharing: true,
    placeholderData: (previous) => previous,
    select: ({ matches }) => createIdMap(matches),
  });

  const journeysById = referencedJourneysQuery.data;

  const rows = useMemo<JourneyRow[]>(
    () =>
      Object.entries(membership).map(([journeyId, state]) => {
        const journey = journeysById?.[journeyId];
        return {
          journeyId,
          title: journey?.title ?? t("Unknown journey"),
          state,
          isArchived: !!journey?.archivedAt,
        };
      }),
    [membership, journeysById, t]
  );

  const scheduledRows = useMemo<ScheduledJourneyRow[]>(
    () =>
      scheduled.map(({ journeyId, addAt }) => ({
        journeyId,
        title: journeysById?.[journeyId]?.title ?? t("Unknown journey"),
        addAt,
      })),
    [scheduled, journeysById, t]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);

  const journeySearchQuery = useTellescopeInfiniteQuery({
    model: "journeys",
    method: "getSome",
    request: {
      limit: 20,
      search: debouncedSearchQuery ? { query: debouncedSearchQuery } : undefined,
    },
    select: (data) => data.pages.flat(),
  });

  const sortedSearchResults = useMemo<TellescopeModelFromName<"journeys">[]>(
    () => [...(journeySearchQuery.data ?? [])].sort((a, b) => (a.archivedAt ? 1 : -1) - (b.archivedAt ? 1 : -1)),
    [journeySearchQuery.data]
  );

  const addOptions = useMemo(
    () =>
      sortedSearchResults.reduce<{ label: string; value: string }[]>((acc, journey) => {
        if (membership[journey.id] === undefined) {
          acc.push({
            label: journey.archivedAt ? t("[ARCHIVED] {{title}}", { title: journey.title }) : journey.title,
            value: journey.id,
          });
        }
        return acc;
      }, []),
    [sortedSearchResults, membership, t]
  );

  const scheduleOptions = useMemo(
    () =>
      sortedSearchResults.reduce<{ label: string; value: string }[]>((acc, journey) => {
        const alreadyScheduled = scheduled.some(({ journeyId }) => journeyId === journey.id);
        if (membership[journey.id] === undefined && !alreadyScheduled) {
          acc.push({
            label: journey.archivedAt ? t("[ARCHIVED] {{title}}", { title: journey.title }) : journey.title,
            value: journey.id,
          });
        }
        return acc;
      }, []),
    [sortedSearchResults, membership, scheduled, t]
  );

  const addToJourneyMutation = useTellescopeMutation({ model: "endusers", method: "add_to_journey" });

  const addJourney = useCallback(
    (journeyId: string) => {
      if (membership[journeyId] !== undefined) return;
      return block.updateEnduserMutation.mutateAsync({
        id: enduserId,
        journeys: { ...membership, [journeyId]: "" },
        _options: { replaceObjectFields: true },
      });
    },
    [membership, enduserId, block.updateEnduserMutation]
  );

  const removeJourney = useCallback(
    (journeyId: string) => {
      const { [journeyId]: _removed, ...remaining } = membership;
      return block.updateEnduserMutation.mutateAsync({
        id: enduserId,
        journeys: remaining,
        _options: { replaceObjectFields: true },
      });
    },
    [membership, enduserId, block.updateEnduserMutation]
  );

  const restartJourney = useCallback(
    (journeyId: string) => {
      if (addToJourneyMutation.isPending) return;
      return addToJourneyMutation.mutateAsync({ enduserIds: [enduserId], journeyId });
    },
    [enduserId, addToJourneyMutation]
  );

  const saveScheduledJourneys = useCallback(
    (next: ScheduledJourney[]) =>
      block.updateEnduserMutation.mutateAsync({
        id: enduserId,
        scheduledJourneys: next,
        _options: { replaceObjectFields: true },
      }),
    [enduserId, block.updateEnduserMutation]
  );

  const scheduleJourney = useCallback(
    (journeyId: string, addAt: Date) => {
      if (scheduled.some((entry) => entry.journeyId === journeyId)) return;
      return saveScheduledJourneys([...scheduled, { journeyId, addAt }]);
    },
    [scheduled, saveScheduledJourneys]
  );

  const unscheduleJourney = useCallback(
    (journeyId: string) => saveScheduledJourneys(scheduled.filter((entry) => entry.journeyId !== journeyId)),
    [scheduled, saveScheduledJourneys]
  );

  const filterBlock = useMemo<FreeformFilterBlock>(
    () => block.parsedInfo?.filter ?? { id: blockId ?? `${viewId}-${blockIndex}`, logic: "and", filters: [] },
    [block.parsedInfo?.filter, blockId, viewId, blockIndex]
  );

  const compiledFilter = useMemo(
    () => compileFreeformFilterBlock(filterBlock, { evalArraysByItem: true }),
    [filterBlock]
  );

  const matchedRows = useMemo(
    () =>
      compiledFilter
        ? rows.filter((row) => applyMongoFilter({ journey: row.title, state: row.state }, compiledFilter))
        : rows,
    [rows, compiledFilter]
  );

  const matchedScheduledRows = useMemo(
    () =>
      compiledFilter
        ? scheduledRows.filter((row) => applyMongoFilter({ journey: row.title, state: "" }, compiledFilter))
        : scheduledRows,
    [scheduledRows, compiledFilter]
  );

  const filterFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [
      {
        value: "journey",
        label: t("Journey"),
        valueType: "tags",
        operations: ["equals", "does_not_equal", "contains", "does_not_contain"],
      },
      {
        value: "state",
        label: t("Step"),
        valueType: "string",
        operations: ["equals", "does_not_equal", "contains", "does_not_contain", "is_set", "is_not_set"],
      },
    ],
    [t]
  );

  const journeyNameOptions = useMemo<string[]>(() => {
    const selected = filterBlock.filters.flatMap((filter) => (Array.isArray(filter.value) ? filter.value : []));
    const enrolledTitles = Object.keys(membership).reduce<string[]>((acc, journeyId) => {
      const title = journeysById?.[journeyId]?.title;
      if (title) acc.push(title);
      return acc;
    }, []);
    return [...new Set([...enrolledTitles, ...sortedSearchResults.map(({ title }) => title), ...selected])];
  }, [membership, journeysById, sortedSearchResults, filterBlock.filters]);

  const onFilterChange = useCallback(
    (next: FreeformFilterBlock) => {
      block.updateInfo({ ...block.parsedInfo, id: block.parsedInfo?.id ?? crypto.randomUUID(), filter: next });
    },
    [block]
  );

  return {
    ...block,
    rows: matchedRows,
    scheduledRows: matchedScheduledRows,
    hasFilter: !!compiledFilter,
    addOptions,
    scheduleOptions,
    searchQuery,
    setSearchQuery,
    isSearching: journeySearchQuery.isLoading,
    addJourney,
    removeJourney,
    restartJourney,
    scheduleJourney,
    unscheduleJourney,
    filterBlock,
    filterFieldOptions,
    journeyNameOptions,
    onFilterChange,
  };
};
